/**
 * useConversationMessages.ts — fetch lifecycle for the "Todas as mensagens"
 * ledger (Fowler's design doc, Slice 2). Mirrors `useConversationDetail`'s
 * shape (loading/ready/error/unauthorized phases, monotonic request id so a
 * stale response can never commit, a stable `refetch`).
 *
 * 404 (unknown/cross-tenant thread) and 502 (upstream down) both surface as
 * the `error` phase with a retry affordance — the task only calls out a
 * dedicated UI for 502, and a 404 here (the thread already resolved via the
 * detail route) is exceptional, so it gets the same clean retry state rather
 * than a second bespoke UI.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiClient } from '../../api/client';
import { UnauthorizedError } from '../../api/client';
import type { MessageRow } from '../../api/types';
import floating from '../../lib/floating';

export type ConversationMessagesPhase = 'loading' | 'ready' | 'error' | 'unauthorized';

export interface ConversationMessagesState {
  messages: MessageRow[];
  phase: ConversationMessagesPhase;
  refetch: () => void;
}

export const useConversationMessages = (
  apiClient: ApiClient,
  threadId: string,
): ConversationMessagesState => {
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [phase, setPhase] = useState<ConversationMessagesPhase>('loading');

  const mountedRef = useRef(true);
  const requestSeq = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetch = useCallback(async () => {
    requestSeq.current += 1;
    const seq = requestSeq.current;
    const isCurrent = () => mountedRef.current && seq === requestSeq.current;
    setPhase('loading');
    try {
      const data = await apiClient.getConversationMessages(threadId);
      if (!isCurrent()) return;
      setMessages(data);
      setPhase('ready');
    } catch (error) {
      if (!isCurrent()) return;
      if (error instanceof UnauthorizedError) {
        setPhase('unauthorized');
        return;
      }
      // 404 / 502 / transport — same clean error+retry state.
      setPhase('error');
    }
  }, [apiClient, threadId]);

  useEffect(() => {
    floating(fetch());
  }, [fetch]);

  return { messages, phase, refetch: fetch };
};
